import type {
  RecordingIntakeAgentMapping,
  RecordingIntakeAgentMappingResponse,
  RecordingIntakeCustomerFailuresCount,
  RecordingIntakeCustomerFailuresCountResponse,
  RecordingIntakeCustomerFailuresCountsByCode,
  RecordingIntakeCustomerFailuresCountsByCodeResponse,
  RecordingIntakeCustomerFailuresPage,
  RecordingIntakeCustomerFailuresPageResponse,
  RecordingIntakeCustomerReprocessResponse,
  RecordingIntakeCustomerReprocessResult,
  RecordingIntakeCustomerSkipResponse,
  RecordingIntakeCustomerSkipResult,
  RecordingIntakeId,
  RecordingIntakeSortOrder,
  RecordingS3IntakeUploadCredentials,
  RecordingS3IntakeUploadCredentialsResponse,
} from "@repo/app-types/entities/recordingIntake";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The customer-facing "Recording S3 intake" surface. The integration row
 * itself rides the boot payload (see `selectRecordingS3IntakeIntegration`);
 * these endpoints back the Failed imports tab and the Settings tab's
 * agent-email mapping and uploader-credentials reveal. Mirrors the Genesys
 * customer endpoints (`genesysIntegration.ts`).
 */

// The failed-imports list + count share this tag, so reprocessing or skipping a
// row refetches both. The agent-email map is read-only and never invalidated.
const FAILED_IMPORTS_TAG_ID = "recording-s3-intake-failed-imports";
const AGENT_MAP_TAG_ID = "recording-s3-intake-agent-map";

export const recordingS3IntakeIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // The Failed imports tab: the caller's own `failed` rows, company-scoped by
    // the server. Both args are part of the cache key so changing either starts a
    // fresh page 1: `order` is the `intaken_at` sort; `errorCode` is the selected
    // chip — `null` = every code, the `__none__` sentinel = rows with no code,
    // else a literal code (forwarded verbatim, the server maps the sentinel back
    // to "no code").
    recordingS3IntakeFailedImports: builder.infiniteQuery<
      RecordingIntakeCustomerFailuresPage,
      { order: RecordingIntakeSortOrder; errorCode: string | null },
      string | null,
      RecordingIntakeCustomerFailuresPageResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/integrations/recording-s3-intake/failed-imports",
        params: {
          order: queryArg.order,
          ...(queryArg.errorCode ? { errorCode: queryArg.errorCode } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The filter chips' counts: `failed`-row counts by `error.code`, scoped to
    // the caller's company and *not* to the selected chip, so picking a chip
    // doesn't refetch (and blank) them. Shares the failed-imports tag, so a
    // reprocess/skip that empties a code refetches this and drops its chip.
    recordingS3IntakeFailedImportsCountsByCode: builder.query<
      RecordingIntakeCustomerFailuresCountsByCode,
      void
    >({
      query: () => "/integrations/recording-s3-intake/failed-imports/counts",
      transformResponse: (
        response: RecordingIntakeCustomerFailuresCountsByCodeResponse,
      ) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The failed count, for the tab badge — its own read so a page of the list
    // isn't needed to know whether the badge should show.
    recordingS3IntakeFailedImportsCount: builder.query<
      RecordingIntakeCustomerFailuresCount,
      void
    >({
      query: () => "/integrations/recording-s3-intake/failed-imports/count",
      transformResponse: (
        response: RecordingIntakeCustomerFailuresCountResponse,
      ) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    reprocessRecordingS3IntakeFailedImports: builder.mutation<
      RecordingIntakeCustomerReprocessResult,
      readonly RecordingIntakeId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/recording-s3-intake/failed-imports/reprocess",
        body: { ids },
      }),
      transformResponse: (response: RecordingIntakeCustomerReprocessResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    skipRecordingS3IntakeFailedImports: builder.mutation<
      RecordingIntakeCustomerSkipResult,
      readonly RecordingIntakeId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/recording-s3-intake/failed-imports/skip",
        body: { ids },
      }),
      transformResponse: (response: RecordingIntakeCustomerSkipResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.RecordingS3IntakeIntegration,
          FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The S3 uploader credentials, decrypted on demand. Fetched lazily by the
    // "Reveal" control only, and never kept around: `keepUnusedDataFor: 0`
    // drops the secret from the store as soon as the component unmounts.
    recordingS3IntakeUploadCredentials: builder.query<
      RecordingS3IntakeUploadCredentials,
      void
    >({
      query: () => "/integrations/recording-s3-intake/upload-credentials",
      transformResponse: (
        response: RecordingS3IntakeUploadCredentialsResponse,
      ) => response.data,
      keepUnusedDataFor: 0,
    }),

    // The read-only RingCX agent username → teammate email map (RingCX customers
    // only). No live-refresh action, unlike the HQ tooling twin.
    recordingS3IntakeAgentEmailMap: builder.query<
      RecordingIntakeAgentMapping,
      void
    >({
      query: () => "/integrations/recording-s3-intake/agent-email-map",
      transformResponse: (response: RecordingIntakeAgentMappingResponse) =>
        response.data,
      providesTags: () => [
        buildEntityTag(TagType.RecordingS3IntakeIntegration, AGENT_MAP_TAG_ID),
      ],
    }),
  }),
});

export const {
  useRecordingS3IntakeFailedImportsInfiniteQuery,
  useRecordingS3IntakeFailedImportsCountQuery,
  useRecordingS3IntakeFailedImportsCountsByCodeQuery,
  useReprocessRecordingS3IntakeFailedImportsMutation,
  useSkipRecordingS3IntakeFailedImportsMutation,
  useRecordingS3IntakeAgentEmailMapQuery,
  useLazyRecordingS3IntakeUploadCredentialsQuery,
} = recordingS3IntakeIntegrationApi;
